import type { MarkingAnchor, MarkingRecord, MarkingSource } from '#shared/utils/markings/schema'
import { markingSourceSchema } from '#shared/utils/markings/schema'
import { parse } from 'valibot'
import { computeMarkableOffsetWithin } from './selection'
import { collectMarkableText, findCitationParent } from './text-filter'
import { readSectionPath } from './section-metadata'

/**
 * `unknown` is the schema's placeholder for a node type we could not read. It
 * says nothing about the content, so a marking leaves the field out entirely
 * rather than storing the placeholder.
 */
export function markingNodeType(nodeType: string | null | undefined): string | undefined {
  return !nodeType || nodeType === 'unknown' ? undefined : nodeType
}

export async function captureMarkingSource(
  range: Range,
  headingId?: string | null,
): Promise<{ source: MarkingSource; anchor: MarkingAnchor }> {
  const root = findCitationParent(range.startContainer)
  const endRoot = findCitationParent(range.endContainer)

  if (!(root instanceof HTMLElement) || root !== endRoot) {
    throw new Error('Select text within a single content unit')
  }

  const attribute = (name: string) => root.getAttribute(name)?.trim() || undefined
  const ancestorIds: string[] = []
  let parent = root.parentElement?.closest<HTMLElement>('[data-marking-unit-id]')

  while (parent) {
    const id = parent.dataset.markingUnitId

    if (id) {
      ancestorIds.unshift(id)
    }

    parent = parent.parentElement?.closest<HTMLElement>('[data-marking-unit-id]')
  }

  const lawId = attribute('data-citation-law-id')
  const contentType = attribute('data-marking-content-type')
  const source = parse(markingSourceSchema, {
    contentType,
    workId: attribute('data-marking-work-id'),
    documentId: attribute('data-marking-document-id'),
    rootDocumentId: attribute('data-marking-root-document-id'),
    unitId: attribute('data-marking-unit-id'),
    ancestorIds: [...new Set([
      ...readSectionPath(root).flatMap(section => section.unitId && section.unitId !== attribute('data-marking-unit-id') ? [section.unitId] : []),
      ...ancestorIds,
    ])],
    headingId: headingId || undefined,
    about:
      contentType === 'commentary' && lawId
        ? {
            lawId,
            unitId: attribute('data-citation-law-fragment-id'),
            dateInForce: attribute('data-citation-date-in-force'),
            lastChangeInForce: attribute('data-citation-last-change-in-force'),
            sourceTimestamp: attribute('data-citation-law-timestamp'),
          }
        : undefined,
    revision: {
      dateInForce: contentType === 'law' ? attribute('data-citation-date-in-force') : undefined,
      lastChangeInForce:
        contentType === 'law' ? attribute('data-citation-last-change-in-force') : undefined,
      editorialUpdatedAt: attribute('data-citation-last-updated'),
      rootEditorialUpdatedAt: attribute('data-marking-root-editorial-updated-at'),
      publishedAt: attribute('data-citation-date-of-publication'),
      editionId: attribute('data-citation-edition-id'),
      previousEditionId: attribute('data-marking-previous-edition-id'),
      sourceTimestamp: attribute('data-citation-timestamp'),
    },
  })
  const startOffset = computeMarkableOffsetWithin(root, range.startContainer, range.startOffset)
  const endOffset = computeMarkableOffsetWithin(root, range.endContainer, range.endOffset)

  if (startOffset < 0 || endOffset <= startOffset) {
    throw new Error('Selection has no markable text')
  }

  // Version 1 uses the reader's noise-filtered text, with unchanged whitespace and UTF-16 offsets.
  const unitText = collectMarkableText(root)
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(unitText))
  const unitTextFingerprint = Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, '0'),
  ).join('')
  return {
    source,
    anchor: { normalizationVersion: 1, unitTextFingerprint, startOffset, endOffset },
  }
}

export function markingRoots(container: Element, marking: Pick<MarkingRecord, 'source'>): Element[] {
  const source = marking.source
  const roots = [container, ...container.querySelectorAll('[data-citation-source]')]
  return roots.filter(
    (root) =>
      root.getAttribute('data-marking-content-type') === source.contentType &&
      root.getAttribute('data-marking-document-id') === source.documentId &&
      root.getAttribute('data-marking-unit-id') === source.unitId,
  )
}

/**
 * The section a marking sits in, as normalized titles from the captured path.
 * `sectionTitle` only covers what the current node renders, so on a book page
 * without its own heading it falls back to `data-citation-title` — the work
 * title — and every such marking would group under the book itself.
 *
 * A repeat of the level above is dropped: a chapter is captured both as a TOC
 * entry and as the heading on its first page, and those two spellings of the
 * same section must not become two groups.
 */
function sectionTrail(marking: Pick<MarkingRecord, 'source'>): string[] {
  const trail: string[] = []

  for (const section of marking.source.sectionPath ?? []) {
    const title = section.title.replace(/\s+/g, ' ').trim()

    if (title && title !== trail[trail.length - 1]) {
      trail.push(title)
    }
  }

  return trail
}

export function markingSectionLabel(marking: Pick<MarkingRecord, 'source' | 'sectionTitle'>): string {
  const trail = sectionTrail(marking)
  return trail[trail.length - 1] || marking.sectionTitle?.trim() || 'Uten avsnitt'
}

export function markingSectionKey(marking: Pick<MarkingRecord, 'source' | 'sectionTitle'>): string {
  const source = marking.source
  // The trail rather than an anchor: article sections hold their subheadings
  // (3.1, 3.2, …) in one unit with no ids, so unitId and headingId cannot tell
  // them apart, while anchors differ between two captures of the same section.
  const trail = sectionTrail(marking)
  const sectionIdentity = trail.length > 0 ? trail : markingSectionLabel(marking)

  if (source.contentType === 'book') {
    return JSON.stringify([
      source.contentType,
      source.workId,
      source.documentId,
      sectionIdentity,
      source.revision,
    ])
  }

  return JSON.stringify([
    source.contentType,
    source.workId,
    source.documentId,
    source.unitId,
    source.headingId,
    sectionIdentity,
    source.revision,
  ])
}
